// src/ui/ReelsRow.jsx — the reels strip, inside the feed.
//
// 🟥 THE BROWSER'S FEED WAS POSTS AND NOTHING ELSE. The phone has had this row
//    since the ranker shipped — a horizontal shelf of clips somewhere in the
//    middle of the column — and it is where most people find video at all,
//    because a video post scrolls past like any other card while a shelf of
//    them asks to be looked at.
//
// 🟢 IT IS A ROW OF POSTERS, NOT A ROW OF PLAYERS. Six autoplaying videos in a
//    feed is six connections, six decoders and somebody's data plan. Each tile
//    is one `preload="metadata"` range request for the frame at `#t=0.1` —
//    a picture of the actual clip rather than a guess at one, which is the same
//    trick the profile grid already uses. Playing happens in the viewer, on a
//    tap, one at a time.
//
// 🟢 AND THE SHELF SCROLLS ON ITS OWN. A strip inside a vertical feed has to
//    own its horizontal axis or the two gestures fight; `overflow-x` with
//    scroll-snap gives the phone gesture and the desktop arrows the same
//    positions to land on.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Play, Video as VideoIcon } from './icons';
import SafeImg from './SafeImg';

/** A clip's own poster: the frame at 0.1s. The first frame is often black. */
const clipOf = (p) => (p?.media || []).find((m) => m?.type === 'video')?.url || p?.videoUrl || null;
const imgOf = (p) => {
  const first = (p?.media || []).find((m) => m?.type === 'image') || (p?.media || [])[0];
  return first?.variants?.medium || first?.variants?.small || p?.imageUrl || null;
};

function Tile({ p, onOpen }) {
  const img = imgOf(p);
  const clip = img ? null : clipOf(p);
  return (
    <button className="rr-tile" onClick={() => onOpen(p)} title={p.content || ''}>
      {img
        ? <SafeImg src={img} kind="inline" loading="lazy" />
        : clip
          ? <video src={`${clip}#t=0.1`} preload="metadata" muted playsInline tabIndex={-1} />
          : <span className="rr-tile-empty"><VideoIcon width={20} height={20} /></span>}
      <span className="rr-tile-play"><Play width={12} height={12} /></span>
      {p.userName ? <span className="rr-tile-who">{p.userName}</span> : null}
    </button>
  );
}

export default function ReelsRow({ items, onOpen, onRefresh }) {
  const railRef = useRef(null);
  const [edge, setEdge] = useState({ start: true, end: false });

  const measure = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setEdge({ start: el.scrollLeft <= 2, end: el.scrollLeft >= max - 2 });
  }, []);

  useEffect(() => { measure(); }, [measure, items]);

  const page = (dir) => {
    const el = railRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(200, el.clientWidth * 0.8), behavior: 'smooth' });
  };

  /* 🟥 AN EMPTY SHELF IS A GAP THE READER SCROLLS PAST TWICE. The placement rule
     already refuses to insert one; this is the second half of the same rule, for
     a row whose items were filtered away after it was placed. */
  if (!items?.length) return null;

  return (
    <section className="rr-card" aria-label="Reels">
      <header className="rr-head">
        <span className="rr-title"><VideoIcon width={16} height={16} /> Reels</span>
        {/* 🟢 THE ONLY CONTROL IS "SHOW ME DIFFERENT ONES", which is the one
            thing a shelf of recommendations can usefully offer. */}
        {onRefresh && (
          <button className="rr-more" onClick={onRefresh} aria-label="Show different reels">⋯</button>
        )}
      </header>
      <div className="rr-rail" ref={railRef} onScroll={measure}>
        {items.map((p) => <Tile key={p.id} p={p} onOpen={onOpen} />)}
      </div>
      {/* 🟢 ARROWS ONLY WHERE THERE IS SOMEWHERE TO GO. A control that cannot do
          anything is worse than no control: it invites a click and answers with
          nothing. On a phone they are hidden entirely — the gesture is the
          affordance there. */}
      {!edge.start && <button className="rr-arrow left" onClick={() => page(-1)} aria-label="Previous">‹</button>}
      {!edge.end && <button className="rr-arrow right" onClick={() => page(1)} aria-label="Next">›</button>}
    </section>
  );
}
